import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { authContext } from "../contexts/AuthProvider";
import { dataContext } from "../contexts/DataProvider";
import { errorContext } from "../contexts/ErrorProvider";
import ProcessData from "../utils/ProcessData";
import validate from "../validation/validator";
import {
  newOrderCustomerDetailsSchema,
  newOrderCustomerAddressSchema,
  newOrderTimeSchema,
} from "../validation/schema";
import {
  fetchAreaList,
  fetchBlockList,
  fetchStreetList,
  calculateDeliveryFee,
} from "../services/locationService";
import { createNewOrder, getPreferredDriverOptions } from "../services/orderService";
import socket from "../socket/socket";
import { formatDecimal } from "../utils/decimalFormat";
import normalize from "../utils/NormalizeSelect";

const NewOrder = ({ popup, setPopup }) => {
  const [step, setStep] = useState("customerDetails");
  const [areas, setAreas] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [streets, setStreets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useContext(dataContext);
  const [errors, setErrors] = useContext(errorContext);
  const [isAuth, , merchantData] = useContext(authContext);
  const [driverOptions, setDriverOptions] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const navigate = useNavigate();

  const preferredDriverEnabled = merchantData?.preferredDriverEnabled === true;

  const processCustomerDetails = () => {
    const validation = validate(
      data,
      setErrors,
      newOrderCustomerDetailsSchema(),
    );
    if (validation === true) {
      setStep("customerAddress");
    }
  };

  const processCustomerAddress = async () => {
    const validation = validate(
      data,
      setErrors,
      newOrderCustomerAddressSchema(),
    );
    if (validation === true) {
      if (data?.status === "scheduled") {
        setStep("orderTime");
      } else if (preferredDriverEnabled) {
        await fetchPreferredDrivers();
        setStep("preferredDriver");
      } else {
        await createOrder();
      }
    }
  };

  const fetchPreferredDrivers = async () => {
    try {
      setDriversLoading(true);
      const response = await getPreferredDriverOptions();
      if (response?.data?.code === 1) {
        setDriverOptions(response?.data?.data?.drivers ?? []);
      } else {
        setDriverOptions([]);
      }
    } catch (err) {
      setDriverOptions([]);
    } finally {
      setDriversLoading(false);
    }
  };

  const processPreferredDriver = async () => {
    await createOrder();
  };

  const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());

  const updateMinTime = (instance, selectedDate) => {
    const now = new Date();
    const isToday =
      selectedDate && selectedDate.toDateString() === now.toDateString();

    if (isToday) {
      instance.set("minTime", now);
    } else {
      instance.set("minTime", "00:00");
    }
  };

  const processOrderTime = async () => {
    const validation = validate(data, setErrors, newOrderTimeSchema());

    if (validation !== true) return;

    if (data?.status === "scheduled" && !isValidDate(data?.scheduledAt)) {
      setErrors((prev) => ({
        ...prev,
        status: "Please select a valid date",
      }));
      return;
    }

    await createOrder();
  };

  const createOrder = async () => {
    try {
      setIsLoading(true);

      const response = await createNewOrder(data);

      if (response?.data?.code === 1) {
        setData({});
        setErrors({});
        setPopup(false);

        if (
          response?.data?.data?.status === "0" &&
          response?.data?.data?.isPreferredDispatch !== true
        ) {
          socket.emit("merchant:sendOrderRequest", response.data.data);
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          order: response?.data?.message,
        }));
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        order: "Something went wrong",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const getAreas = async () => {
    const response = await fetchAreaList();
    if (response.data.code === 1) {
      setAreas(response?.data?.data);
    }
  };

  const getBlocks = async (area) => {
    const areaName = encodeURIComponent(area?.value);
    const response = await fetchBlockList(areaName);
    if (response.data.code === 1) {
      setBlocks(response?.data?.data);
    }
  };

  const getStreets = async (block) => {
    const areaName = encodeURIComponent(block?.area);
    const blockName = encodeURIComponent(block?.value);
    console.log(blockName);
    const response = await fetchStreetList(areaName, blockName);
    if (response.data.code === 1) {
      setStreets(response?.data?.data);
    }
  };

  const calculateFee = async (street) => {
    const endLatitude = street?.latitude;
    const endLongitude = street?.longitude;
    if (endLatitude && endLongitude) {
      setIsLoading(true);
      const response = await calculateDeliveryFee(endLatitude, endLongitude);
      if (response.data.code === 1) {
        setData((prev) => ({
          ...prev,
          time: response.data.data.duration,
          distance: response.data.data.distance,
          feeInt: response.data.data.feeInt ?? 0,
          fee: response.data.data.fee ?? 0,
        }));
        setIsLoading(false);
      } else {
        setErrors({ ...errors, order: response?.data?.message });
        setIsLoading(false);
      }
    }
  };

  const reset = useCallback(() => {
    setStep("customerDetails");
    setBlocks([]);
    setStreets([]);
    setDriverOptions([]);
    setData({
      status: "0",
    });
    setErrors({});
  }, [setData, setErrors]);

  useEffect(() => {
    if (isAuth === true) {
      setIsLoading(false);
    }
    if (isAuth === false) {
      navigate("/");
    }
  }, [isAuth, navigate, setIsLoading]);

  useEffect(() => {
    getAreas();
  }, []);

  useEffect(() => {
    if (!popup) {
      reset();
    }
  }, [popup, reset]);

  return (
    <div
      className={
        popup === true
          ? "modal fade effect-slide-in-bottom show bg-success-transparent"
          : "modal fade effect-slide-in-bottom"
      }
      style={popup === true ? { display: "block" } : {}}
      data-bs-backdrop="static"
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content modal-content-demo">
          <div className="modal-header">
            <h6 className="modal-title">New Order</h6>
            <button
              onClick={() => {
                setPopup((prev) => !prev);
              }}
              aria-label="Close"
              className="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div className="modal-body p-0">
            <div className="row mx-0">
              <div className="col-md-12 mt-3">
                {errors?.order && (
                  <div className="alert alert-solid-danger alert-dismissible fade show">
                    {errors?.order}
                    <button
                      onClick={() => setErrors({ ...errors, order: null })}
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="alert"
                      aria-label="Close"
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                )}
              </div>
              <div className="col-md-12">
                {step === "customerDetails" ? (
                  <div className="card border-0 mb-0">
                    <div className="card-body p-3">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-2">
                            <label className="form-label">CUSTOMER PHONE</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter phone number"
                              autoComplete="off"
                              value={data?.customerPhone ?? ""}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                ProcessData(
                                  data,
                                  setData,
                                  value,
                                  "number",
                                  "customerPhone",
                                  errors,
                                  setErrors,
                                );
                              }}
                            />
                            <small className="text-danger">
                              {errors?.customerPhone ?? ""}
                            </small>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <label className="form-label">CUSTOMER NAME</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter full name"
                              autoComplete="off"
                              value={data?.customerName ?? ""}
                              onChange={(e) => {
                                ProcessData(
                                  data,
                                  setData,
                                  e,
                                  "string",
                                  "customerName",
                                  errors,
                                  setErrors,
                                );
                              }}
                            />
                            <small className="text-danger">
                              {errors?.customerName ?? ""}
                            </small>
                          </div>
                        </div>
                        <div className="col-md-9">
                          <div className="mb-2">
                            <label className="form-label">PAYMENT TYPE</label>
                            <div className="w-100">
                              <div
                                className="btn-group w-100"
                                role="group"
                                aria-label="Basic radio toggle button group"
                              >
                                <input
                                  type="radio"
                                  className="btn-check"
                                  name="paymentType"
                                />
                                <label
                                  className={
                                    data?.paymentType === "Cash on Delivery"
                                      ? "btn btn-outline-light bg-primary text-light"
                                      : "btn btn-outline-light"
                                  }
                                  onClick={() => {
                                    ProcessData(
                                      data,
                                      setData,
                                      "Cash on Delivery",
                                      "radio",
                                      "paymentType",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                >
                                  Cash on Delivery
                                </label>
                                <input
                                  type="radio"
                                  className="btn-check"
                                  name="paymentType"
                                />
                                <label
                                  className={
                                    data?.paymentType === "Paid"
                                      ? "btn btn-outline-light bg-primary text-light"
                                      : "btn btn-outline-light"
                                  }
                                  onClick={() => {
                                    ProcessData(
                                      data,
                                      setData,
                                      "Paid",
                                      "radio",
                                      "paymentType",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                >
                                  Paid
                                </label>
                                <input
                                  type="radio"
                                  className="btn-check"
                                  name="paymentType"
                                />
                                <label
                                  className={
                                    data?.paymentType === "Payment Link"
                                      ? "btn btn-outline-light bg-primary text-light"
                                      : "btn btn-outline-light"
                                  }
                                  onClick={() => {
                                    ProcessData(
                                      data,
                                      setData,
                                      "Payment Link",
                                      "radio",
                                      "paymentType",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                >
                                  Payment Link
                                </label>
                              </div>
                              <small className="text-danger">
                                {errors?.paymentType ?? ""}
                              </small>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="mb-2">
                            <label className="form-label">AMOUNT</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="0.000"
                              autoComplete="off"
                              value={data?.orderAmount ?? ""}
                              onChange={(e) => {
                                ProcessData(
                                  data,
                                  setData,
                                  e,
                                  "string",
                                  "orderAmount",
                                  errors,
                                  setErrors,
                                );
                              }}
                            />
                            <small className="text-danger">
                              {errors?.orderAmount ?? ""}
                            </small>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="mb-2">
                            <label className="form-label">ORDER TIMING</label>
                            <div className="w-100">
                              <div
                                className="btn-group w-100"
                                role="group"
                                aria-label="Basic radio toggle button group"
                              >
                                <input
                                  type="radio"
                                  className="btn-check"
                                  name="status"
                                  value="0"
                                />
                                <label
                                  className={
                                    data?.status === "0"
                                      ? "btn btn-outline-light bg-primary text-light"
                                      : "btn btn-outline-light"
                                  }
                                  onClick={() => {
                                    ProcessData(
                                      data,
                                      setData,
                                      "0",
                                      "radio",
                                      "status",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                >
                                  Instant Order
                                </label>
                                <input
                                  type="radio"
                                  className="btn-check"
                                  name="status"
                                  value="scheduled"
                                />
                                <label
                                  className={
                                    data?.status === "scheduled"
                                      ? "btn btn-outline-light bg-primary text-light"
                                      : "btn btn-outline-light"
                                  }
                                  onClick={() => {
                                    ProcessData(
                                      data,
                                      setData,
                                      "scheduled",
                                      "radio",
                                      "status",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                >
                                  Scheduled Order
                                </label>
                              </div>
                              <small className="text-danger">
                                {errors?.status ?? ""}
                              </small>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="mb-2">
                            <label className="form-label">
                              ORDER NUMBER/DESCRIPTION
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Order number or description"
                              autoComplete="off"
                              value={data?.description ?? ""}
                              onChange={(e) => {
                                ProcessData(
                                  data,
                                  setData,
                                  e,
                                  "string",
                                  "description",
                                  errors,
                                  setErrors,
                                );
                              }}
                            />
                            <small className="text-danger">
                              {errors?.description ?? ""}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer border-top text-center">
                      <div className="ms-auto">
                        <button
                          onClick={processCustomerDetails}
                          className="btn btn-primary"
                        >
                          Next: Customer Address
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                {step === "customerAddress" ? (
                  <div className="card border-0 mb-0">
                    <div className="card-body p-3">
                      <div className="row">
                        <div className="col-md-4 text-center my-auto">
                          <div
                            className="mx-auto d-flex justify-content-center align-items-center rounded-circle border border-3 border-primary shadow mb-3"
                            style={{ width: "200px", height: "200px" }}
                          >
                            <div className="text-center">
                              <div className="fw-bolder h6 fs-30">
                                {data?.time ?? 0}
                              </div>
                              <div className="fw-bolder h6 fs-15">MINS</div>
                            </div>
                          </div>
                          <div className="text-center">
                            <button className="btn btn-primary-light btn-wave waves-effect waves-light d-block mb-3 mx-auto">
                              Distance: {data?.distance ?? 0}km
                            </button>
                            <button className="btn btn-primary-light btn-wave waves-effect waves-light d-block mx-auto">
                              Delivery Fee: {formatDecimal(data?.feeInt)}
                            </button>
                          </div>
                        </div>
                        <div className="col-md-8">
                          <div className="row">
                            <div className="col-md-8">
                              <div className="mb-2">
                                <label className="form-label">AREA</label>
                                <Select
                                  className="react-select-styled react-select-bordered react-select-sm"
                                  classNamePrefix="react-select"
                                  value={
                                    areas
                                      ?.map((item) => ({
                                        value: item?.value,
                                        label: item?.value,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value === data?.area?.value,
                                      ) ?? null
                                  }
                                  onChange={async (e) => {
                                    ProcessData(
                                      data,
                                      setData,
                                      e,
                                      "select",
                                      "area",
                                      errors,
                                      setErrors,
                                    );
                                    setBlocks([]);
                                    setData((prev) => ({
                                      ...prev,
                                      block: null,
                                      street: null,
                                    }));
                                    await getBlocks(e?.value);
                                  }}
                                  options={areas?.map((item) => {
                                    return {
                                      value: item,
                                      label: item?.value,
                                    };
                                  })}
                                  filterOption={(option, input) => {
                                    return normalize(option.label).includes(normalize(input));
                                  }}
                                  placeholder="Select area"
                                />
                                <small className="text-danger">
                                  {errors?.area ?? ""}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="mb-2">
                                <label className="form-label">BLOCK</label>
                                <Select
                                  className="react-select-styled react-select-bordered react-select-sm"
                                  classNamePrefix="react-select"
                                  value={
                                    blocks
                                      ?.map((item) => ({
                                        value: item?.value,
                                        label: item?.value,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value === data?.block?.value,
                                      ) ?? null
                                  }
                                  onChange={async (e) => {
                                    ProcessData(
                                      data,
                                      setData,
                                      e,
                                      "select",
                                      "block",
                                      errors,
                                      setErrors,
                                    );
                                    setStreets([]);
                                    setData((prev) => ({
                                      ...prev,
                                      street: null,
                                    }));
                                    await getStreets(e?.value);
                                  }}
                                  options={blocks?.map((item) => {
                                    return {
                                      value: item,
                                      label: item?.value,
                                    };
                                  })}
                                  filterOption={(option, input) => {
                                    return normalize(option.label).includes(normalize(input));
                                  }}
                                  placeholder="Select block"
                                />
                                <small className="text-danger">
                                  {errors?.block ?? ""}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-12">
                              <div className="mb-2">
                                <label className="form-label">STREET</label>
                                <Select
                                  className="react-select-styled react-select-bordered react-select-sm"
                                  classNamePrefix="react-select"
                                  value={
                                    streets
                                      ?.map((item) => ({
                                        value: item?.value,
                                        label: item?.value,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value === data?.street?.value,
                                      ) ?? null
                                  }
                                  onChange={async (e) => {
                                    ProcessData(
                                      data,
                                      setData,
                                      e,
                                      "select",
                                      "street",
                                      errors,
                                      setErrors,
                                    );
                                    await calculateFee(e?.value);
                                  }}
                                  options={streets?.map((item) => {
                                    return {
                                      value: item,
                                      label: item?.value,
                                    };
                                  })}
                                  filterOption={(option, input) => {
                                    return normalize(option.label).includes(normalize(input));
                                  }}
                                  placeholder="Select street"
                                />
                                <small className="text-danger">
                                  {errors?.street ?? ""}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-2">
                                <label className="form-label">
                                  BUILDING NUMBER OR NAME
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Building number/ name"
                                  autoComplete="off"
                                  value={data?.building ?? ""}
                                  onChange={(e) => {
                                    ProcessData(
                                      data,
                                      setData,
                                      e,
                                      "string",
                                      "building",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                />
                                <small className="text-danger">
                                  {errors?.building ?? ""}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-2">
                                <label className="form-label">JADDA</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter jadda"
                                  autoComplete="off"
                                  value={data?.jadda ?? ""}
                                  onChange={(e) => {
                                    ProcessData(
                                      data,
                                      setData,
                                      e,
                                      "string",
                                      "jadda",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                />
                                <small className="text-danger">
                                  {errors?.jadda ?? ""}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-2">
                                <label className="form-label">
                                  APARTMENT/ OFFICE
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Appartment or office"
                                  autoComplete="off"
                                  value={data?.apartment ?? ""}
                                  onChange={(e) => {
                                    ProcessData(
                                      data,
                                      setData,
                                      e,
                                      "string",
                                      "apartment",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                />
                                <small className="text-danger">
                                  {errors?.apartment ?? ""}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-2">
                                <label className="form-label">FLOOR</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Floor"
                                  autoComplete="off"
                                  value={data?.floor ?? ""}
                                  onChange={(e) => {
                                    ProcessData(
                                      data,
                                      setData,
                                      e,
                                      "string",
                                      "floor",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                />
                                <small className="text-danger">
                                  {errors?.floor ?? ""}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-12">
                              <div className="mb-2">
                                <label className="form-label">NOTES</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Notes/ instruction"
                                  autoComplete="off"
                                  value={data?.notes ?? ""}
                                  onChange={(e) => {
                                    ProcessData(
                                      data,
                                      setData,
                                      e,
                                      "string",
                                      "notes",
                                      errors,
                                      setErrors,
                                    );
                                  }}
                                />
                                <small className="text-danger">
                                  {errors?.notes ?? ""}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer border-top">
                      <div className="ms-auto d-flex justify-content-between">
                        <button
                          onClick={() => {
                            setStep("customerDetails");
                          }}
                          className="btn btn-primary"
                        >
                          Prev: Customer Details
                        </button>
                        <button
                          onClick={processCustomerAddress}
                          className="btn btn-primary"
                          disabled={isLoading}
                        >
                          {data?.status === "scheduled" ? (
                            "Next: Order Time"
                          ) : preferredDriverEnabled ? (
                            "Next: Select Driver"
                          ) : isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-xs me-1"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Create Order
                            </>
                          ) : (
                            "Create Order"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                {step === "preferredDriver" &&
                  data?.status !== "scheduled" &&
                  preferredDriverEnabled ? (
                  <div className="card border-0 mb-0">
                    <div className="card-body p-3">
                      <div className="row">
                        <div className="col-md-12">
                          <label className="form-label">
                            PREFERRED DRIVER (OPTIONAL)
                          </label>
                          <p className="text-muted fs-12">
                            Choose a driver to assign this order directly to
                            them, or leave unselected to dispatch
                            automatically to nearby drivers.
                          </p>
                          {driversLoading ? (
                            <div className="text-center my-3">
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </div>
                          ) : (
                            <Select
                              className="react-select-styled react-select-bordered"
                              classNamePrefix="react-select"
                              isClearable
                              placeholder="Automatic dispatch (no preferred driver)"
                              options={driverOptions.map((driver) => ({
                                label: `${driver?.name ?? "Driver"} • ${driver?.vehicleType ?? ""
                                  } • ${driver?.distanceKm ?? "?"} km • ${driver?.online ? "Online" : "Offline"
                                  }${driver?.busy ? " • Busy" : ""}`,
                                value: driver?.driverId,
                              }))}
                              value={
                                data?.preferredDriverId
                                  ? {
                                    label:
                                      driverOptions.find(
                                        (d) =>
                                          d?.driverId ===
                                          data?.preferredDriverId,
                                      )?.name ?? "Selected driver",
                                    value: data?.preferredDriverId,
                                  }
                                  : null
                              }
                              onChange={(selected) => {
                                setData((prev) => ({
                                  ...prev,
                                  preferredDriverId: selected?.value ?? null,
                                }));
                              }}
                              noOptionsMessage={() =>
                                "No drivers found in the configured zone"
                              }
                            />
                          )}
                          <small className="text-danger">
                            {errors?.order ?? ""}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer border-top">
                      <div className="ms-auto d-flex justify-content-between">
                        <button
                          onClick={() => {
                            setStep("customerAddress");
                          }}
                          className="btn btn-primary"
                        >
                          Prev: Customer Address
                        </button>
                        <button
                          onClick={processPreferredDriver}
                          className="btn btn-primary"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span
                              className="spinner-border spinner-border-xs me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            ""
                          )}
                          Create Order
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                {step === "orderTime" && data?.status === "scheduled" ? (
                  <div className="card border-0 mb-0">
                    <div className="card-body p-3">
                      <div className="row">
                        <div className="col-md-6 mx-auto">
                          <div className="mb-2">
                            <label className="form-label">ORDER TIME</label>
                            <div className="w-100">
                              <div className="flatpickr-wrapper position-relative">
                                <Flatpickr
                                  className="hidden-flatpickr-input"
                                  value={data?.scheduledAt || null}
                                  onChange={(date) => {
                                    setData((prev) => ({
                                      ...prev,
                                      scheduledAt: date[0],
                                    }));
                                    setErrors((prev) => ({
                                      ...prev,
                                      status: null,
                                    }));
                                  }}
                                  options={{
                                    inline: true,
                                    enableTime: true,
                                    dateFormat: "Y-m-d H:i",
                                    minDate: new Date(),
                                    minuteIncrement: 1,
                                    onReady: (
                                      selectedDates,
                                      dateStr,
                                      instance,
                                    ) => {
                                      updateMinTime(instance, selectedDates[0]);
                                    },
                                    onChange: (
                                      selectedDates,
                                      dateStr,
                                      instance,
                                    ) => {
                                      updateMinTime(instance, selectedDates[0]);
                                    },
                                  }}
                                />
                                {data?.status !== "scheduled" && (
                                  <div className="flatpickr-blocker" />
                                )}
                              </div>

                              <small className="text-danger">
                                {errors?.status ?? ""}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer border-top">
                      <div className="ms-auto d-flex justify-content-between">
                        <button
                          onClick={() => {
                            setStep("customerAddress");
                          }}
                          className="btn btn-primary"
                        >
                          Prev: Customer Address
                        </button>
                        <button
                          onClick={processOrderTime}
                          className="btn btn-primary"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span
                              className="spinner-border spinner-border-xs me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            ""
                          )}
                          Create Order
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewOrder;
